'use client';

import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { BonusShopApi, BonusShopFs } from '@/api/cms/bonusShop';
import { TabNavigation } from '@/components/TabNavigation';
import { ScreenType } from '@/helpers/enums';
import { useResponsive } from '@/modules/client';
import { ReceiveBonusesNotice } from '@/modules/common';
import { useGetCommonStore } from '@/store';
import { ScMainContainer } from '@/styles/common';
import { BonusShopBalance } from './components/BonusShopBalance';
import { BonusShopGrid } from './components/BonusShopGrid';
import { BonusShopSections } from './components/BonusShopSections';
import { BonusShopTerms } from './components/BonusShopTerms';
import { BonusShopGridSkeleton } from './components/skeletons/BonusShopGridSkeleton';
import { BonusShopSectionsSkeleton } from './components/skeletons/BonusShopSectionsSkeleton';
import { BonusShopTab, getTabs } from './helpers';
import { useBonusShopProducts } from './hooks';
import {
  ScBonusShopNotice,
  ScBonusShopPageContainer,
  ScBonusShopTitle,
} from './styled';

const SECTION_COUNT = 5;

interface BonusShopProps {
  shop: BonusShopApi;
  freeSpins: BonusShopFs[];
}

export const BonusShop = ({ shop, freeSpins }: BonusShopProps) => {
  const { t } = useTranslation('bonusShop');
  const [tabId, setTabId] = useState<BonusShopTab>(BonusShopTab.ALL);
  const [isTermsExpanded, setIsTermsExpanded] = useState(false);
  const termsRef = useRef<HTMLDivElement>(null);
  const { currentScreenType } = useResponsive();
  const isMobileLayout = currentScreenType < ScreenType.MD;

  const useCommonStore = useGetCommonStore();
  const pendingUser = useCommonStore.use.pendingUser();

  const { products } = useBonusShopProducts({ freeSpins, shop });
  const generalTerms = shop.generalTerms || '';

  const tabs = useMemo(() => getTabs(t), [t]);

  const handleTermsClick = useCallback(() => {
    setIsTermsExpanded(true);
    termsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const handleShowAll = useCallback((tab: BonusShopTab) => {
    setTabId(tab);
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const isAllTab = tabId === BonusShopTab.ALL;

  const renderProducts = () => {
    if (pendingUser && !products.length) {
      return isAllTab ? (
        <BonusShopSectionsSkeleton sections={SECTION_COUNT} />
      ) : (
        <BonusShopGridSkeleton rows={2} />
      );
    }

    return isAllTab ? (
      <BonusShopSections onShowAll={handleShowAll} products={products} />
    ) : (
      <BonusShopGrid products={products} tab={tabId} />
    );
  };

  const tabNavigationProps = useMemo(() => {
    if (isMobileLayout) return { fullScreen: true };
    return {
      maxWidth100: true,
      withoutShadow: true,
      menuItemWidth: `${100 / tabs.length}%`,
    };
  }, [isMobileLayout, tabs.length]);

  return (
    <ScMainContainer>
      <ScBonusShopPageContainer>
        <div className="heading">
          <ScBonusShopTitle>{t('title')}</ScBonusShopTitle>
          <BonusShopBalance
            onTermsClick={generalTerms ? handleTermsClick : undefined}
          />
        </div>

        <ScBonusShopNotice>
          <ReceiveBonusesNotice
            successMessage="canIssueBonuses.message.success"
            successTitle="canIssueBonuses.title.success"
            translation="loyalty"
            warningMessage="canIssueBonuses.message.warning"
            warningTitle="canIssueBonuses.title.warning"
          />
        </ScBonusShopNotice>

        <TabNavigation
          navItems={tabs}
          selectedItem={tabId}
          setSelectedItem={(index) => setTabId(index as BonusShopTab)}
          withoutShadow
          {...tabNavigationProps}
        />

        {renderProducts()}

        <BonusShopTerms
          content={generalTerms}
          isExpanded={isTermsExpanded}
          onToggle={() => setIsTermsExpanded((expanded) => !expanded)}
          ref={termsRef}
        />
      </ScBonusShopPageContainer>
    </ScMainContainer>
  );
};
